import React, { useState } from 'react'
import { Plus } from 'lucide-react'
import { DreamService, type Dream, type UpdateDreamRequest } from '../../../services/dreamService'
import { InlinePopover, ChipRow } from '../../tasks/inline/InlinePopover'
import { DREAM_PRIORITY_OPTIONS } from '../../../utils/dreamOptions'
import { priorityWord } from '../../../utils/dreamWords'
import { LifeAreasPicker } from '../../ui/LifeAreasPicker'
import { Timestamp } from '../../ui/Timestamp'

/**
 * A dream's facts, each edited where it is shown (Dreams UX pass 2026-09-06,
 * the task page's philosophy): the title, why it matters, by when, which
 * parts of life, priority. A dream has no parent and no done-means. With
 * these the edit page has nothing left to offer and leaves.
 */
interface EditorProps {
  dream: Dream
  onChanged: (dream: Dream) => void
  trigger?: React.ReactNode
  variant?: 'dotted' | 'chip' | 'icon'
  className?: string
  openRequest?: number
}

async function write(dream: Dream, payload: UpdateDreamRequest, onChanged: (d: Dream) => void) {
  const svc = new DreamService()
  await svc.updateDream(dream.id, payload)
  onChanged(await svc.getDream(dream.id))
}

const inputClass = 'w-full rounded-md border border-soft bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30'

// ── Progress: the percentage in Keeping up is the trigger (only while the
// dream has no goals — with goals, the figure is their average and is read).
// The page carried a "Progress" panel that showed the STORED figure (10%)
// under a Keeping up block showing the goals' average (55%) — two numbers for
// one fact (North Star rule 2, 2026-09-12).
const PROGRESS_CHIPS = [0, 25, 50, 75, 100].map((n) => ({ value: String(n), label: n === 100 ? 'Done' : `${n}%` }))

export function DreamProgressEditor({ dream, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(String(Math.round(Number(dream.progress || 0))))
  const save = async () => {
    const n = Math.round(Number(value))
    if (!Number.isFinite(n) || n < 0 || n > 100) throw new Error('A number from 0 to 100.')
    await write(dream, n >= 100 ? { progress: 100, status: 'completed' } : { progress: n }, onChanged)
  }
  return (
    <InlinePopover label="How far along" variant={variant} className={className} onOpen={reset} onSave={save} testId="dream-edit-progress" width="w-80"
                   trigger={trigger ?? `${Math.round(Number(dream.progress || 0))}%`}>
      <ChipRow label="How far along" options={PROGRESS_CHIPS} value={PROGRESS_CHIPS.some((c) => c.value === value) ? value : ''} onChange={setValue} />
      <input type="number" min={0} max={100} step={5} value={value} onChange={(e) => setValue(e.target.value)} aria-label="Percent" className={`${inputClass} w-28`} />
    </InlinePopover>
  )
}

export function DreamTitleEditor({ dream, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(dream.title || '')
  const save = async () => {
    const text = value.trim()
    if (!text) throw new Error('A dream needs a name.')
    if (text === (dream.title || '').trim()) return
    await write(dream, { title: text }, onChanged)
  }
  return (
    <InlinePopover label="Title" variant={variant} className={className} onOpen={reset} onSave={save} testId="dream-edit-title" width="w-96" trigger={trigger ?? dream.title}>
      <input type="text" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Title" autoFocus className={inputClass} placeholder="Name the life you want — Feel strong at fifty, Work I am proud of" />
    </InlinePopover>
  )
}

export function DreamWhyEditor({ dream, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [text, setText] = useState('')
  const current = dream.description
  const reset = () => setText(current || '')
  const save = async () => { await write(dream, { description: text.trim() || null }, onChanged) }
  return (
    <InlinePopover label="Why it matters" variant={variant} className={className} onOpen={reset} onSave={save} testId="dream-edit-description" width="w-96"
                   trigger={trigger ?? (current || <><Plus className="h-3 w-3" /> why it matters</>)}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} aria-label="Why it matters" autoFocus placeholder="What your life looks like when this is true — one line is plenty" className={`${inputClass} resize-y`} />
      {current && <button type="button" onClick={() => setText('')} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Clear it</button>}
    </InlinePopover>
  )
}

export function DreamTargetDateEditor({ dream, onChanged, trigger, variant = 'dotted', className, openRequest }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue((dream.target_date || '').slice(0, 10))
  const save = async () => { await write(dream, { target_date: value || null }, onChanged) }
  return (
    <InlinePopover label="Get there by" variant={variant} className={className} onOpen={reset} onSave={save} testId="dream-edit-target-date" openRequest={openRequest}
                   trigger={trigger ?? (dream.target_date ? <>by <Timestamp value={`${dream.target_date.slice(0, 10)}T12:00:00`} mode="date" /></> : <><Plus className="h-3 w-3" /> by when</>)}>
      <input type="date" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Get there by" className={inputClass} />
      <p className="text-app-micro text-muted">A rough date is fine — a season, a year. When it passes, I will ask how it is going, nothing more.</p>
      {dream.target_date && <button type="button" onClick={() => setValue('')} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">No date</button>}
    </InlinePopover>
  )
}

export function DreamPriorityEditor({ dream, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(dream.priority || 'medium')
  const save = async () => { await write(dream, { priority: (value || 'medium') as UpdateDreamRequest['priority'] }, onChanged) }
  return (
    <InlinePopover label="Priority" variant={variant} className={className} onOpen={reset} onSave={save} testId="dream-edit-priority" trigger={trigger ?? priorityWord(dream.priority)}>
      <ChipRow label="Priority" options={DREAM_PRIORITY_OPTIONS} value={value} onChange={setValue} />
    </InlinePopover>
  )
}

/** The life areas, where they are shown (the summary line) and in the Add row. */
export function DreamAreasEditor({ dream, onChanged, editable = true, testId = 'dream-life-area' }: { dream: Dream; onChanged: (d: Dream) => void; editable?: boolean; testId?: string }) {
  const value = dream.life_areas && dream.life_areas.length ? dream.life_areas : dream.life_area ? [dream.life_area] : []
  return (
    <LifeAreasPicker value={value} editable={editable} testId={testId}
                     onSave={async (values) => { await write(dream, { life_areas: values }, onChanged) }} />
  )
}

/** The "Add" row: the facts not yet set, related ones together. */
export function DreamAddRow({ dream, onChanged }: { dream: Dream; onChanged: (dream: Dream) => void }) {
  const plan: React.ReactNode[] = []
  if (!(dream.life_areas && dream.life_areas.length) && !dream.life_area) plan.push(<span key="areas" className="inline-flex items-center gap-1 text-app-micro"><DreamAreasEditor dream={dream} onChanged={onChanged} testId="dream-add-areas" /></span>)
  if (!dream.target_date) plan.push(<DreamTargetDateEditor key="by" dream={dream} onChanged={onChanged} variant="chip" />)
  if (dream.priority !== 'high' && dream.priority !== 'critical') plan.push(<DreamPriorityEditor key="priority" dream={dream} onChanged={onChanged} variant="chip" trigger={<>priority · {priorityWord(dream.priority)}</>} />)
  const carry: React.ReactNode[] = []
  if (!dream.description) carry.push(<DreamWhyEditor key="why" dream={dream} onChanged={onChanged} variant="chip" />)
  const groups = [plan, carry].filter((g) => g.length > 0)
  if (groups.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="dream-add-details">
      <span className="text-app-micro text-muted">Add</span>
      {groups.map((g, i) => <span key={i} className="flex flex-wrap items-center gap-1.5" data-testid="dream-add-group">{g}</span>)}
    </div>
  )
}
